import {
  KatAccordion,
  KatAccordionItem,
  KatPanel,
  KatPanelWrapper,
  KatSpinner,
  KatToggle,
} from "@amzn/katal-react";
import React, { useState, useEffect } from "react";
import classNames from "classnames";
import styles from "src/components/Pages/DocumentInbound/DocumentComplianceHelper/styles.module.scss";
import { useValidationResults } from "src/components/Contexts/ValidationResultsContext";
import { useTranslation } from "react-i18next";
import {
  ComplianceHelperField,
  extractAllValidationResults,
  getCriticalErrorsCount,
} from "./document-compliance-helper-extractor";
import ComplianceErrorItem from "./ComplianceErrorItem";
import ComplianceErrorSolution from "./ComplianceErrorSolution";
import { ValidationRuleSeverity } from "src/api/interfaces/document_validation";
import { useDocumentCompliance } from "src/components/Contexts/DocumentComplianceContext";
import CustomPopover from "src/components/CustomPopover/CustomPopover";
import { useDocumentComplianceHelper } from "src/components/Pages/DocumentInbound/DocumentComplianceHelper/DocumentComplianceHelperContext";

const SMALL_SCREEN_BREAKPOINT = 1680;

const isCriticalError = (severity: string): boolean => {
  return [
    ValidationRuleSeverity.CRITICAL.valueOf(),
    ValidationRuleSeverity.SEVERE.valueOf(),
    ValidationRuleSeverity.ERROR.valueOf(),
  ].includes(severity);
};

interface DocumentComplianceHelperPanelProps {}

const DocumentComplianceHelperPanel: React.FC<
  DocumentComplianceHelperPanelProps
> = () => {
  const { isVisible, setVisible } = useDocumentComplianceHelper();
  const { validationResultsByGroup } = useValidationResults();
  const documentCompliance = useDocumentCompliance();
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [selectedErrorIndex, setSelectedErrorIndex] = useState(0);
  const [groupedValidationResults, setGroupedValidationResults] = useState<
    Record<string, ComplianceHelperField[]>
  >({});
  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth < SMALL_SCREEN_BREAKPOINT
  );
  const [hasInitializedToggle, setHasInitializedToggle] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () =>
      setIsSmallScreen(window.innerWidth < SMALL_SCREEN_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggleChange = () => {
    setShowAllErrors(!showAllErrors);
    setSelectedErrorIndex(0);
  };

  const getTitle = () => {
    return `${t(
      "aic_doc_page_inbound_document_compliance_helper_title"
    )} - ${getCriticalErrorsCount(groupedValidationResults)}
    ${t("aic_doc_page_inbound_document_compliance_helper_subtitle")}`;
  };

  const getAllErrors = (): ComplianceHelperField[] => {
    return Object.values(groupedValidationResults).flat();
  };

  const getFilteredErrors = (): ComplianceHelperField[] => {
    const allErrors = getAllErrors();
    return showAllErrors
      ? allErrors
      : allErrors.filter((item) => isCriticalError(item.severity));
  };

  const hasResults = (): boolean => {
    return (
      groupedValidationResults &&
      Object.keys(groupedValidationResults).length > 0 &&
      getAllErrors().length > 0
    );
  };

  useEffect(() => {
    if (validationResultsByGroup && documentCompliance) {
      const resultsByGroup = extractAllValidationResults(
        validationResultsByGroup,
        documentCompliance,
        t
      );
      setGroupedValidationResults(resultsByGroup);
    }
  }, [validationResultsByGroup, documentCompliance, t]);

  useEffect(() => {
    if (!hasInitializedToggle && hasResults()) {
      const allErrors = getAllErrors();
      const criticalErrors = allErrors.filter((item) =>
        isCriticalError(item.severity)
      );

      if (allErrors.length > 0 && criticalErrors.length === 0) {
        setShowAllErrors(true);
      }

      setHasInitializedToggle(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedValidationResults, hasInitializedToggle]);

  return (
    <KatPanelWrapper size={isSmallScreen ? "large" : "small"}>
      <KatPanel
        visible={isVisible}
        className={classNames(styles.document_compliance_helper)}
        header={getTitle()}
        onClose={() => setVisible(false)}
      >
        <div
          className={classNames(
            styles.document_compliance_helper_subtitle_container
          )}
        >
          <KatToggle
            checked={showAllErrors}
            textChecked={t(
              "aic_doc_page_inbound_document_compliance_helper_toggle_hide_errors"
            )}
            textUnchecked={t(
              "aic_doc_page_inbound_document_compliance_helper_toggle_show_errors"
            )}
            onChange={() => handleToggleChange()}
          />
          <CustomPopover />
        </div>
        {hasResults() ? (
          <KatAccordion>
            {getFilteredErrors().map((item, index) => (
              <KatAccordionItem
                id={index.toString()}
                expanded={selectedErrorIndex === index}
                className={classNames(styles.accordion_item)}
                label={
                  <ComplianceErrorItem
                    key={index}
                    item={item}
                    index={index}
                    onClick={() => setSelectedErrorIndex(index)}
                  />
                }
              >
                <ComplianceErrorSolution selectedItem={item} />
              </KatAccordionItem>
            ))}
          </KatAccordion>
        ) : (
          <div className={classNames(styles.load_spinner_container)}>
            <KatSpinner />
          </div>
        )}
      </KatPanel>
    </KatPanelWrapper>
  );
};

export default DocumentComplianceHelperPanel;
